import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import _ from 'lodash';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import cn from 'classnames';
import { useParams } from 'react-router-dom';
import { flowType } from '@utility/userInfo';
import { ReactComponent as UpArrow } from '@assets/images/upArrow.svg';
import { ReactComponent as DownArrow } from '@assets/images/Modifiersdropdown.svg';
import { TouchableDiv } from '@components/common/Touchable';
import { getSelections, handleModSelect } from './ItemModifierHelper';
import style from './ItemModifierGroup.module.scss';
import ItemModifier from './ItemModifier/ItemModifier';
import store from '@store';
const isKiosk = flowType() === 'kiosk';
import { getObjectValue } from '@utility/utills';
import { LABEL } from '@veriables';

const ItemModifierGroup = ({
  modifierGroup,
  modifierGroupsHashTable,
  modifierDataForGroup,
  modifiersHashTable,
  nestedModifierArray,
  serverAppTheme,
  isLoading,
  isVerticalLayout,
  isNestedModifier,
  hasActiveFilter,
  userExpanded,
  userCollapsed,
  onToggle,
}) => {
  const count = useRef(0);
  const [collapse, setCollapse] = useState(() => {
    if (userExpanded) return false;
    if (userCollapsed) return true;
    if (modifierGroup?.minimum > 0) return false;
    return getObjectValue(() => modifierGroup?.displayStyle?.collapsed, false);
  });

  const preFilterCollapseRef = useRef(null);

  useEffect(() => {
    if (hasActiveFilter) {
      preFilterCollapseRef.current = collapse;
      setCollapse(false);
    } else {
      if (preFilterCollapseRef.current !== null) {
        setCollapse(preFilterCollapseRef.current);
        preFilterCollapseRef.current = null;
      }
    }
  }, [hasActiveFilter]);
  const [visible, setVisible] = useState(false);
  const { menuItemId } = useParams();
  const [selectedModArray, setSelectedModArray] = useState(() => []);
  const [activeGroupName, setActiveGroupName] = useState({});
  const [modCount, setModCount] = useState(true);
  const ref = useRef(null);

  const modifierGroupList = useMemo(() => {
    if (!modifierGroup) return [];
    return _.sortBy(
      _.chain(modifierGroup.modifierLinks)
        .groupBy('groupName')
        .map((value, key) => ({ groupName: key, items: value }))
        .value(),
      [d => (d.groupName === 'null' ? 1 : 0), d => (d.items && d.items.length > 0 ? d.items[0].index : 0)]
    );
  }, [modifierGroup]);

  const handleObserver = useCallback((entries, observe) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        setVisible(true);
        observe.unobserve(entry.target);
      }
    });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: document.querySelector('#modifiersContainer'),
      rootMargin: '0px',
      threshold: [0.1, 0.9],
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [handleObserver]);

  useEffect(() => {
    if (modCount) {
      const modifierData = store.getState().orders.modifierData;
      const result = handleModSelect(
        modifierGroup,
        modifiersHashTable,
        modifierGroupsHashTable,
        nestedModifierArray,
        modifierData,
        selectedModArray,
        count.current
      );
      if (result) {
        setSelectedModArray(prev =>
          prev.length === result.modArray.length &&
            prev.every((v, i) => v === result.modArray[i])
            ? prev
            : result.modArray
        );
        count.current = result.countTemp;
        setCollapse(prev => {
          if (prev && !result.collapse && count.current === 0 && modifierGroup.minimum === 0) {
            return prev;
          }
          const next = prev === result.collapse ? prev : result.collapse;
          if (next) {
            setActiveGroupName({});
          }
          return next;
        });
      }
    }
  }, [modifierDataForGroup, menuItemId]);

  const isModifierRequired = modifierGroup.minimum > 0;

  const hasSubGroups = useMemo(
    () =>
      modifierGroupList.some(g => g.groupName !== 'null' && g.groupName !== ''),
    [modifierGroupList]
  );

  const handlePillClick = useCallback((groupName, e) => {
    e.preventDefault();
    e.stopPropagation();
    setCollapse(false);
    setActiveGroupName(prev => ({ [groupName]: !prev[groupName] }));
  }, []);

  const handleCollapseToggle = useCallback(e => {
    e.stopPropagation();
    e.preventDefault();
    setActiveGroupName({});
    setCollapse(prev => {
      onToggle && onToggle(modifierGroup.id, !prev);
      return !prev;
    });
  }, [modifierGroup.id, onToggle]);

  const hasNullGroup = useMemo(
    () => modifierGroupList.some(g => g.groupName === 'null' && g.items?.length > 0),
    [modifierGroupList]
  );

  const pillsSection = hasSubGroups ? (
    <div
      className={cn(
        'd-flex flex-row o-group-menu overflowX overflowY align-items-center py-1',
        !isVerticalLayout && 'w-100'
      )}
      style={isVerticalLayout ? { width: '100%' } : undefined}
    >
      {modifierGroupList.map((el, index) =>
        el.groupName !== 'null' && el.groupName !== '' ? (
          <TouchableDiv
            key={el.groupName}
            style={{
              minHeight: '26px',
              borderRadius: '104px',
              color: activeGroupName[el.groupName]
                ? '#FFF'
                : serverAppTheme?.primary_text,
              border: activeGroupName[el.groupName] ? 'none' : `1px solid #BEBEBE`,
              backgroundColor: activeGroupName[el.groupName]
                ? '#74A4FF'
                : '#F1F1F1',
            }}
            className={cn(
              'flex-shrink-0 inline-flex text-nowrap py-1 fs_12 d-flex align-items-center mr-1 fw-600',
              isVerticalLayout ? 'px-1' : 'px-2',
            )}
            tabIndex="0"
            role="button"
            onClick={e => handlePillClick(el.groupName, e)}
            rippleEffect={true}
          >
            {isVerticalLayout
              ? el.groupName.length > 3
                ? el.groupName.substring(0, 3)
                : el.groupName
              : el.groupName.length > 9
                ? `${el.groupName.substring(0, 9)}...`
                : el.groupName}
          </TouchableDiv>
        ) : null
      )}
      {hasNullGroup && (
        <TouchableDiv
          key="others"
          style={{
            minHeight: '26px',
            borderRadius: '104px',
            color: activeGroupName['null'] ? '#FFF' : serverAppTheme?.primary_text,
            border: activeGroupName['null'] ? 'none' : `1px solid #BEBEBE`,
            backgroundColor: activeGroupName['null'] ? '#74A4FF' : '#F1F1F1',
          }}
          className={cn(
            'flex-shrink-0 inline-flex text-nowrap py-1 fs_12 d-flex align-items-center mr-1 fw-600',
            isVerticalLayout ? 'px-1' : 'px-2',
          )}
          tabIndex="0"
          role="button"
          onClick={e => handlePillClick('null', e)}
          rippleEffect={true}
        >
          {LABEL().OTHERS}
        </TouchableDiv>
      )}
    </div>
  ) : null;

  const itemModifier =
    modifierGroup.modifierLinks?.length !== 0 || !isLoading ? (
      <ItemModifier
        modifierGroupList={modifierGroupList}
        activeGroupName={activeGroupName}
        collapse={isVerticalLayout ? false : collapse}
        setCollapse={setCollapse}
        modifierGroup={modifierGroup}
        isModifierRequired={isModifierRequired}
        setModCount={setModCount}
        selectedModArray={selectedModArray}
        setSelectedModArray={setSelectedModArray}
        count={count}
        isVerticalLayout={isVerticalLayout}
      />
    ) : (
      <div style={{ height: '20px' }}>
        Loading{' '}
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className={style.bar}
            style={{ animationDelay: `${i * 0.1}s` }}
          />
        ))}
      </div>
    );

  return (
    <div
      ref={ref}
      className={cn(
        isModifierRequired
          ? style.modifierContainer
          : style.optionModifierContainer,
        isVerticalLayout && 'mr-1 py-1 px-1 d-flex flex-column h-100',
      )}
      style={{
        backgroundColor: isModifierRequired ? serverAppTheme?.requiredModifierGroupBg : '#F1F1F1',
        ...(isVerticalLayout ? { width: '137px', flexShrink: 0 , border: '1px solid #DADADA'} : {}),
      }}
    >
      {isVerticalLayout ? (
        <>
          <div
            className="fs-14 fw-700 d-flex justify-content-around"
            style={{ width: '121px' }}
            modifier-group={modifierGroup.id}
            id={modifierGroup.id}
          >
            <div className={cn(style.modifierNameContainer)}>
              <span
                className="fs_14 fw-500 pb-1 text-center"
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'block',
                }}
              >
                {modifierGroupsHashTable[modifierGroup.id]?.name?.length > 14
                  ? `${modifierGroupsHashTable[
                    modifierGroup.id
                  ]?.name?.substring(0, 14)}...`
                  : modifierGroupsHashTable[modifierGroup.id]?.name || ''}
              </span>
              <div className="fs_10 fw-500 text-center mb-1">
                {getSelections(modifierGroup)}
              </div>
            </div>
          </div>
          {pillsSection}
          <div
            className="overflowY"
            style={{ flex: 1, overflowY: 'auto' }}
          >
            {itemModifier}
          </div>
        </>
      ) : (
        <>
          <TouchableDiv
            className="fs-14 fw-700 d-flex flex-column"
            onClick={handleCollapseToggle}
            role="presentation"
            modifier-group={modifierGroup.id}
            id={modifierGroup.id}
          >
            <div
              className={cn(
                'd-flex align-items-center p-1',
                isKiosk && 'fs-14',
                style.modifierNameContainer
              )}
              style={{ backgroundColor: isModifierRequired ? serverAppTheme?.requiredModifierGroupBg : '#F1F1F1', }}
            >
              <span className="fs_12 fw-500">
                {modifierGroupsHashTable[modifierGroup.id]?.name || ''}
              </span>
              <div className="d-flex align-items-center">
                <div className="mr-0">
                  <span
                    className={cn(
                      'pt-1 pull-right fs_12 fw-500',
                      style.requiredModifierCount
                    )}
                  >
                    {getSelections(modifierGroup)}
                  </span>
                </div>
                <span className="px-1 d-flex align-items-center">
                  {collapse ? <DownArrow /> : <UpArrow /> }
                </span>
              </div>
            </div>
          </TouchableDiv>
          <div
            className="px-1"
          >
            {collapse && selectedModArray?.length > 0 && (
              <div className="d-flex flex-column">
                {selectedModArray.map(item => (
                  <span
                    key={item}
                    className={cn(
                      'fw-500',
                      isKiosk ? 'fs-20 ml-3' : 'ml-2 fs_12'
                    )}
                  >
                    {item || ''}
                  </span>
                ))}
              </div>
            )}
            {pillsSection}
            {itemModifier}
          </div>
        </>
      )}
    </div>
  );
};

const mapStateToProps = ({ orders, theme }, ownProps) => {
  const groupId = ownProps.modifierGroup?.id;
  const modifierDataForGroup = groupId
    ? orders.modifierData
      .filter(d => d.endsWith(`-${groupId}`) || d.includes(`-${groupId}|`))
      .join(',')
    : '';
  return {
    modifierGroupsHashTable: orders.modifierGroupsHashTable,
    modifierDataForGroup,
    modifiersHashTable: orders.modifiersHashTable,
    nestedModifierArray: orders.nestedModifierArray,
    serverAppTheme: theme.serverAppTheme,
    isNestedModifier: orders.isNestedModifier,
  };
};

const mapDispatchToProps = dispatch => bindActionCreators({}, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(React.memo(ItemModifierGroup));
